/**
 * TestOpsCommandLogo — canonical visual mark for TestOps Command.
 *
 * Stylised "[>_]" — terminal/command-prompt glyph that matches the
 * "Command" half of the brand. Four-color by default:
 *   - left bracket  [  → orange  (warm side)
 *   - chevron       >  → pink    (prompt)
 *   - underscore    _  → violet  (cursor)
 *   - right bracket ]  → purple  (cool side)
 *
 * The asymmetric warm-left / cool-right gradient gives the mark some
 * motion without a backplate.
 *
 * Pass `monochrome` to render every stroke in `currentColor` instead, for
 * places that need a single-color treatment (e.g. embedded in a wordmark
 * that already provides its own palette).
 *
 * Hosts that want a different mark (Athena's owl, Toucan's bird, etc.)
 * pass their own component as `AvatarComponent` to <TestOpsCommand>.
 */
export interface TestOpsCommandLogoProps {
  size?: number
  className?: string
  /** Render every path in `currentColor` instead of the brand palette. */
  monochrome?: boolean
}

export function TestOpsCommandLogo({ size = 24, className, monochrome = false }: TestOpsCommandLogoProps) {
  const leftBracketColor  = monochrome ? 'currentColor' : '#F97316'   // orange-500
  const rightBracketColor = monochrome ? 'currentColor' : '#9333EA'   // purple-600
  const chevronColor      = monochrome ? 'currentColor' : '#EC4899'   // pink-500
  const cursorColor       = monochrome ? 'currentColor' : '#8B5CF6'   // violet-500

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="TestOps Command"
      role="img"
    >
      {/* Left bracket [ */}
      <path
        d="M 6.5 3.5 L 3.5 3.5 L 3.5 20.5 L 6.5 20.5"
        stroke={leftBracketColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Right bracket ] */}
      <path
        d="M 17.5 3.5 L 20.5 3.5 L 20.5 20.5 L 17.5 20.5"
        stroke={rightBracketColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Greater-than chevron > (prompt indicator) */}
      <path
        d="M 8 8.5 L 12 12 L 8 15.5"
        stroke={chevronColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Underscore _ (cursor) */}
      <path
        d="M 13.5 16 L 16.5 16"
        stroke={cursorColor}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}
